import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useWalletStore } from '../store/walletStore'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { useQuinielaStore } from '../store/quinielaStore'
import { Trophy, TrendingUp, Wallet, Target, Calendar, Users } from 'lucide-react'
import CompleteProfileModal from '../components/CompleteProfileModal'

export default function Dashboard() {
  const { userData } = useAuthStore()
  const { balance, transactions, fetchBalance, fetchTransactions } = useWalletStore()
  const { quinielas, loading, fetchAvailableQuinielas, checkUserParticipation } = useQuinielaStore()
  const [activeQuinielas, setActiveQuinielas] = useState([])
  const [activeAciertos, setActiveAciertos] = useState(0)
  const [aciertosPorParticipacion, setAciertosPorParticipacion] = useState([])
  const [showCompleteProfile, setShowCompleteProfile] = useState(false)

  // Check if user needs to complete profile
  useEffect(() => {
    if (userData && userData.profileComplete === false) {
      setShowCompleteProfile(true)
    }
  }, [userData])

  useEffect(() => {
    fetchBalance()
    fetchAvailableQuinielas()
    fetchTransactions(5)
  }, [fetchBalance, fetchAvailableQuinielas, fetchTransactions])

  useEffect(() => {
    async function loadActiveQuinielas() {
      if (!userData?.id) return
      // Filtrar quinielas en progreso
      const enProgreso = quinielas.filter(q => q.status === 'open' || q.status === 'upcoming')
      let userActiveQuinielas = []
      let totalAciertos = 0
      let aciertosDetalle = []
      for (const q of enProgreso) {
        const participation = await checkUserParticipation(q.id, userData.id)
        if (participation.isParticipant && participation.participations.length > 0) {
          userActiveQuinielas.push(q)
          // Puede haber más de una participación por quiniela
          participation.participations.forEach((p, idx) => {
            aciertosDetalle.push({
              quinielaName: q.name,
              quinielaId: q.id,
              aciertos: p.correctPredictions || 0,
              participationId: p.id,
              idx: idx + 1
            })
            totalAciertos += p.correctPredictions || 0
          })
        }
      }
      setActiveQuinielas(userActiveQuinielas)
      setActiveAciertos(totalAciertos)
      setAciertosPorParticipacion(aciertosDetalle)
    }
    loadActiveQuinielas()
  }, [quinielas, userData])

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-kiniela-bg min-h-screen">
      {/* Welcome Banner */}
      <div className="bg-kiniela-card rounded-2xl p-8 k-left-accent mb-8">
        <h1 className="text-3xl font-bold mb-2 text-kiniela-text">
          ¡Bienvenido, <span className="text-kiniela-gold">{userData?.fullName || userData?.username}</span>!
        </h1>
        <p className="text-kiniela-muted">
          Listos para la siguiente jornada. Revisa las quinielas disponibles.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 mt-12">
        {/* Wallet Balance */}
        <div className="k-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-kiniela-text">Saldo Total</h3>
            <Wallet className="text-kiniela-gold" />
          </div>
          <p className="text-3xl font-bold text-kiniela-gold">
            ${balance?.total?.toFixed(2) || '0.00'}
          </p>
          <div className="mt-4 flex justify-between text-sm">
            <div>
              <p className="text-kiniela-muted">Depositado</p>
              <p className="font-semibold text-kiniela-text">${balance?.deposited?.toFixed(2) || '0.00'}</p>
            </div>
            <div>
              <p className="text-kiniela-muted">Ganado</p>
              <p className="font-semibold text-kiniela-success">${balance?.winnings?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
          <Link to="/wallet" className="btn bg-kiniela-cta text-kiniela-text hover:bg-kiniela-ctaHover w-full mt-6 text-center">
            Ver Billetera
          </Link>
        </div>

        {/* Quinielas Activas */}
        <div className="k-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-kiniela-text">Quinielas en Proceso</h3>
            <Trophy className="text-kiniela-gold" />
          </div>
          <p className="text-3xl font-bold text-kiniela-text">{activeQuinielas.length}</p>
          <p className="text-sm text-kiniela-muted mt-2">Participaciones en progreso</p>
          <Link to="/quinielas" className="k-btn-secondary w-full mt-6 text-center">
            Ver Todas
          </Link>
        </div>

        {/* Aciertos */}
        <div className="k-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-kiniela-text">Aciertos</h3>
            <Target className="text-kiniela-muted" />
          </div>
          <p className="text-3xl font-bold text-kiniela-text">{activeAciertos}</p>
          <p className="text-sm text-kiniela-muted mt-2">Total en tus participaciones activas</p>
          {aciertosPorParticipacion.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-kiniela-gold mb-1">Desglose:</h4>
              <ul className="text-sm text-kiniela-text space-y-1">
                {aciertosPorParticipacion.map((item, i) => (
                  <li key={item.participationId}>
                    <span className="font-semibold">{item.quinielaName}</span>
                    {aciertosPorParticipacion.filter(x => x.quinielaId === item.quinielaId).length > 1 && (
                      <span> (Participación {item.idx})</span>
                    )}
                    : {item.aciertos} acierto{item.aciertos === 1 ? '' : 's'}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Posición Promedio */}
        <div className="k-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-kiniela-text">Mejor Posición</h3>
            <TrendingUp className="text-kiniela-muted" />
          </div>
          <p className="text-3xl font-bold text-kiniela-text">-</p>
          <p className="text-sm text-kiniela-muted mt-2">En quinielas finalizadas</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-16">
        {/* Available Quinielas */}
        <div className="k-card">
          <h2 className="text-2xl font-bold mb-4 k-title">Quinielas Disponibles</h2>
          <div className="space-y-4">
            {loading ? (
              <div className="border border-kiniela-border rounded-lg p-4 text-center">
                <p className="text-kiniela-text">Cargando quinielas...</p>
              </div>
            ) : quinielas.length === 0 ? (
              <div className="border border-kiniela-border rounded-lg p-4 text-center flex flex-col items-center">
                <p className="text-kiniela-muted">No hay quinielas disponibles en este momento</p>
                <Link to="/quinielas" className="k-btn-primary mt-4 text-center w-full">
                  Explorar Quinielas
                </Link>
              </div>
            ) : (
              <>
                {quinielas.slice(0, 3).map((quiniela) => (
                  <Link
                    key={quiniela.id}
                    to={`/quinielas/${quiniela.id}`}
                    className="block border border-kiniela-border rounded-lg p-4 hover:border-kiniela-gold hover:shadow-md transition-all bg-kiniela-bg"
                  >
                    <h3 className="font-semibold text-lg mb-2 text-kiniela-gold">{quiniela.name}</h3>
                    <div className="flex items-center gap-4 text-sm text-kiniela-muted">
                      <div className="flex items-center gap-1">
                        <Trophy size={16} />
                        <span>${quiniela.entryFee}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users size={16} />
                        <span>{quiniela.participants?.count || 0} participantes</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar size={16} />
                        <span>Jornada {quiniela.jornada}</span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <span className="text-sm font-semibold text-kiniela-gold">
                        Pozo: ${quiniela.prizePool?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                  </Link>
                ))}
                {quinielas.length > 3 && (
                  <Link to="/quinielas" className="k-btn-secondary w-full text-center">
                    Ver todas ({quinielas.length})
                  </Link>
                )}
              </>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="k-card">
          <h2 className="text-2xl font-bold mb-4 k-title">Actividad Reciente</h2>
          <div className="space-y-4">
            {transactions && transactions.length > 0 ? (
              transactions.slice(0, 5).map((tx) => (
                <div key={tx.id} className="border border-kiniela-border bg-kiniela-bg rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between">
                  <div>
                    <span className="font-semibold capitalize text-kiniela-text">{tx.type === 'deposit' ? 'Depósito' : tx.type === 'bet' ? 'Apuesta' : tx.type === 'win' ? 'Premio' : tx.type}</span>
                    <span className="ml-2 text-kiniela-muted">{tx.description ? `- ${tx.description}` : ''}</span>
                  </div>
                  <div className="flex flex-col md:items-end mt-2 md:mt-0">
                    <span className={`font-bold ${['deposit', 'win', 'prize'].includes(tx.type) ? 'text-kiniela-success' : 'text-kiniela-cta'}`}>{tx.amount > 0 ? '+' : ''}${tx.amount?.toFixed(2) || '0.00'}</span>
                    <span className="text-xs text-kiniela-muted">{tx.createdAt && (tx.createdAt instanceof Date ? format(tx.createdAt, 'PPP p', { locale: es }) : format(tx.createdAt.toDate(), 'PPP p', { locale: es }))}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="border border-kiniela-border rounded-lg p-4 text-center">
                <p className="text-kiniela-muted">No hay actividad reciente</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Complete Profile Modal */}
      {showCompleteProfile && (
        <CompleteProfileModal
          onComplete={() => setShowCompleteProfile(false)}
        />
      )}
    </div>
  )
}
